import { getTeamMember, CLINIC } from "@/data/content";
import { HAIRLINE_PLANNING } from "@/data/photos";

export const pickPrimary = (ids = []) => {
  const members = ids.map(getTeamMember).filter(Boolean);
  return members.find((m) => m.group === "physicians") || members[0] || null;
};

// Consultation ladder v2.2 — offer line is chosen by PAGE TYPE, not by practitioner.
export const OFFERS = {
  general: "Consultation with a physician — 45 minutes, $250, redeemable against treatment within 30 days.",
  skin: "Skin assessment with Dr Kiran Shahid — 45 minutes, $250, redeemable against treatment.",
  hair: "Hair assessment with Dr Kristy Truong — 45 minutes, $250, redeemable against treatment within 30 days.",
  advanced: "Consultation with a physician — 45 minutes, $250, redeemable against treatment within 30 days.",
  nurse: "Skin consultation with Lizzie Stock — 45 minutes, $100, redeemable against treatment.",
};

const TREATMENT_OFFER = { "threads-and-regenerative": "advanced", "skin-and-laser": "nurse", "minor-procedures": "skin" };
export const offerForTreatment = (slug) => OFFERS[TREATMENT_OFFER[slug] || "general"];

export const offerForConcern = (family, slug, primary) => {
  if (primary?.id === "lizzie") return OFFERS.nurse;
  if (slug === "correcting-previous-work") return OFFERS.advanced;
  if (family === "skin") return OFFERS.skin;
  if (family === "hair") return OFFERS.hair;
  return OFFERS.general;
};

export const variantForConcern = (family) => (family === "skin" ? "skin" : family === "hair" ? "hair" : "procedure");
export const variantForTreatment = (slug) =>
  ["skin-and-laser", "minor-procedures"].includes(slug) ? "skin" : "procedure";

export const PROOF_SECOND = { skin: "Examined before treated", hair: "Diagnosis first", procedure: "Planned by one doctor" };

export const CONCERN_OVERRIDES = {
  "life-stages/your-first-treatment": {
    member: "truong",
    offer: OFFERS.general,
  },
  "hair/male-pattern": {
    links: [{ label: "Read: Understanding the Norwood scale", to: "/journal/understanding-the-norwood-scale" }, { label: "Hair assessment and medical treatment", to: "/hair/treatment" }],
  },
  "hair/female-thinning": {
    links: [{ label: "Read: Hair loss in women", to: "/journal/hair-loss-in-women" }, { label: "Hair assessment and medical treatment", to: "/hair/treatment" }],
  },
  "hair/hairline-and-temples": {
    links: [{ label: "Hair transplantation", to: "/hair/transplantation" }, { label: "Is a transplant right for you?", to: "/hair/transplantation/is-it-right-for-you" }],
  },
  "hair/eyebrows": {
    links: [{ label: "Brow, beard and hairline areas", to: "/hair/transplantation/areas" }, { label: "Is a transplant right for you?", to: "/hair/transplantation/is-it-right-for-you" }],
  },
  "hair/scalp-health": {
    links: [{ label: "Hair assessment and medical treatment", to: "/hair/treatment" }, { label: "Read: How hair grows", to: "/journal/how-hair-grows" }],
  },
};

export const HAIR_HERO_IMAGE = { id: HAIRLINE_PLANNING.id, pos: HAIRLINE_PLANNING.pos };
export const SKIN_HERO_IMAGE = { id: "/images/scenes/skin-assessment.webp", pos: "58% 40%" };
export const ROOM_HERO_IMAGE = { id: "/images/treatment-room.webp", pos: "50% 42%" };

export const heroImageFor = (member, variant) => {
  if (!member) return ROOM_HERO_IMAGE;
  if (variant === "hair" && member.id === "truong") return HAIR_HERO_IMAGE;
  if (variant === "skin" && member.id === "shahid") return SKIN_HERO_IMAGE;
  if (member.heroImage) return member.heroImage;
  if (member.photo) return { id: member.photo, pos: "50% 22%" };
  return ROOM_HERO_IMAGE;
};

const joinNames = (members) => {
  const names = members.map((m) => m.name);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}` : names[0];
};

export const fillFaqs = (faqs, who, offer, target = 5) => {
  const members = (who || []).map(getTeamMember).filter(Boolean);
  const generic = [
    { q: "Do I need a referral?", a: "No. Anyone can request a consultation directly. If your concern needs a GP or a specialist instead, we will say so and point you to the right place." },
    { q: "What happens at a first visit?", a: "Up to forty-five minutes with your practitioner: your history, an examination, and an honest plan with costs in writing. No treatment at a first visit." },
    { q: "Is the consultation fee redeemable?", a: `Yes. ${offer}` },
    { q: "Who will I see?", a: `Your first consultation is with ${joinNames(members) || "one of our physicians"}. A doctor assesses you before any medical treatment, and every treatment plan is made and overseen by a doctor.` },
    { q: "How private is a visit?", a: "Lumière practises inside an established Cottesloe medical centre, so arriving here looks like any other visit to the building. Appointments are spaced so the waiting room stays quiet, and your records stay within the practice." },
    { q: "How do I book?", a: `Use the form on this page, call ${CLINIC.phone}, or request a consultation from any page — the practice confirms every request personally.` },
  ];
  const out = [...faqs];
  for (const g of generic) {
    if (out.length >= target) break;
    if (!out.some((f) => f.q.toLowerCase() === g.q.toLowerCase())) out.push(g);
  }
  return out;
};
